/// <amd-module name="VSS/Ext/Components/ExternalContent/ExternalContentBase" />
import * as React from "react";
import * as ExtResources from "VSS/Ext/Resources";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { css, IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { getExtensionMarketplaceUrl, isExtensionFallbackEnabled, useExtensionFallback } from "../../ExternalContentUtil";
import {
    IExternalContentBaseProps,
    IExternalContentInteractionProvider,
    IExternalContentLoadedEvent,
    IExternalContentLoadingErrorEvent
} from "./ExternalContent.Props";

const enum LoadDurations {
    showLoadingIndicator = 250
}

export interface IExternalContentState {
    loading: boolean;
    currentUrl: string;
    showLoadingIndicator?: boolean;
    loadError?: IExternalContentLoadingErrorEvent;
    themeless?: boolean;
}

export class ExternalContentBase extends React.Component<IExternalContentBaseProps, IExternalContentState> {
    public static getDerivedStateFromProps(props: IExternalContentBaseProps, state: IExternalContentState): IExternalContentState | null {
        if (props.url !== state.currentUrl) {
            return {
                loading: true,
                currentUrl: props.url
            };
        }

        return null;
    }
    private showLoadingIndicatorTimeout: number;
    private interactionProvider: IExternalContentInteractionProvider | undefined;

    public constructor(props: IExternalContentBaseProps, context?: IVssComponentContext) {
        super(props, context);

        this.state = {
            loading: !!props.getInteractionProvider,
            showLoadingIndicator: false,
            currentUrl: props.url
        };
    }

    public render(): JSX.Element {
        const { className, renderLoadingIndicator, renderLoadError, url } = this.props;
        const { loading, showLoadingIndicator, loadError, themeless } = this.state;

        return (
            <div className={css("external-content flex-column relative", className)}>
                {loadError && (renderLoadError ? renderLoadError(loadError) : <ExternalContentError loadError={loadError} />)}
                {showLoadingIndicator &&
                    (renderLoadingIndicator ? (
                        renderLoadingIndicator()
                    ) : (
                        <div className="flex flex-grow flex-center justify-center">
                            <WrappedComponent wrappedType="spinner" size="large" label={ExtResources.loading} />
                        </div>
                    ))}
                {url && (
                    <iframe
                        className={css("external-content--iframe flex-grow", loading && "invisible", themeless && "themeless")}
                        role="presentation"
                        frameBorder={0}
                        ref={this.resolveIFrameRef}
                        src={url}
                    />
                )}
            </div>
        );
    }

    public componentDidMount() {
        this.updateLoadingIndicator();
    }

    public componentDidUpdate(prevProps: IExternalContentBaseProps, prevState: IExternalContentState) {
        this.updateLoadingIndicator();
    }

    public componentWillUnmount() {
        this.disposeInteractionProvider();
    }

    private updateLoadingIndicator() {
        if (!this.showLoadingIndicatorTimeout && this.state.loading) {
            this.showLoadingIndicatorTimeout = window.setTimeout(() => {
                this.setState({ showLoadingIndicator: true });
            }, LoadDurations.showLoadingIndicator);
        }
    }

    private clearLoadingIndicator() {
        window.clearTimeout(this.showLoadingIndicatorTimeout);
        this.showLoadingIndicatorTimeout = 0;
    }

    private disposeInteractionProvider() {
        if (this.interactionProvider) {
            this.interactionProvider.dispose();
            this.interactionProvider = undefined;
        }
    }

    private resolveIFrameRef = (iframe: HTMLIFrameElement | null): void => {
        const { getInteractionProvider } = this.props;

        this.disposeInteractionProvider();

        if (iframe && getInteractionProvider) {
            this.interactionProvider = getInteractionProvider(iframe);
            if (this.interactionProvider) {
                this.interactionProvider.subscribe((loadedEvent: IExternalContentLoadedEvent) => {
                    if (this.props.onLoaded) {
                        this.props.onLoaded(loadedEvent);
                    }
                    this.clearLoadingIndicator();
                    this.setState({
                        loading: false,
                        showLoadingIndicator: false,
                        themeless: !loadedEvent.themed
                    });
                }, "loaded");

                this.interactionProvider.subscribe((loadError: IExternalContentLoadingErrorEvent) => {
                    if (!isExtensionFallbackEnabled()) {
                        // Disable CDN for the next attempt in case that is the reason for
                        // the extension load failure. Note that we aren't auto-reloading here.
                        useExtensionFallback();
                    }

                    this.clearLoadingIndicator();
                    this.setState({
                        loadError: loadError,
                        loading: !loadError.loadComplete,
                        showLoadingIndicator: !loadError.loadComplete
                    });
                }, "loadError");
            }
        }
    };
}

export interface IExternalContentErrorProps {
    loadError: IExternalContentLoadingErrorEvent;
}

export interface IExternalContentErrorState {
    extensionUrl?: string;
}

export class ExternalContentError extends VssComponent<IExternalContentErrorProps, IExternalContentErrorState> {
    constructor(props: IExternalContentErrorProps, context?: IVssComponentContext) {
        super(props, context);
        this.state = {};
    }

    public componentDidMount() {
        super.componentDidMount();

        getExtensionMarketplaceUrl(this.context.pageContext, this.props.loadError.extensionId).then(extensionUrl => {
            this.setState({ extensionUrl });
        });
    }

    public render(): JSX.Element {
        const { loadError } = this.props;
        const { extensionUrl } = this.state;

        return (
            <FormatComponent
                className={css("external-content-message font-size-m", !loadError.loadComplete ? "warning" : "error")}
                format={loadError.messageFormat}
                elementType="span"
            >
                <a href={extensionUrl}>{loadError.extensionName}</a>
            </FormatComponent>
        );
    }
}
